import { RangeSpan } from 'shared/excel/rangeSpan';
import { rangeAddressToIndexes, rangeIndexesToString } from 'shared/excel/excel';


export class RangeAddress {
  static Limit = {
    RowCount: 1048576, // 1:1048576 for the OfficeWeb
    ColumnCount: 16384, // A:XFD for the OfficeWeb - 0:16383
  };

  get rowIndex() { return this.rowSpan.index; }
  get rowCount() { return this.rowSpan.count; }
  get rowEndIndex() { return this.rowSpan.endIndex; }

  get columnIndex() { return this.columnSpan.index; }
  get columnCount() { return this.columnSpan.count; }
  get columnEndIndex() { return this.columnSpan.endIndex; }

  get isSingleCell() { return this.columnCount === 1 && this.rowCount === 1; }
  get isEntireRow() { return this.rowSpan.isUnbound || this.rowCount >= RangeAddress.Limit.RowCount; }
  get isEntireColumn() { return this.columnSpan.isUnbound || this.columnCount >= RangeAddress.Limit.ColumnCount; }
  get isUnbound() { return this.isEntireRow || this.isEntireColumn; }
  get isEntireSheet() { return this.isEntireRow && this.isEntireColumn; }
  get cellsCount() { return this.isUnbound ? Number.POSITIVE_INFINITY : this.rowCount * this.columnCount; }

  get worksheetName() { return this.sheet; }

  get address() {
    if (!this._address) {
      this._address = rangeIndexesToString(this);
    }
    return this._address;
  }

  get shortAddress() { // Without worksheet name
    if (!this._shortAddress) {
      this._shortAddress = rangeIndexesToString({
        rowIndex: this.rowIndex, columnIndex: this.columnIndex,
        rowCount: this.rowCount, columnCount: this.columnCount });
    }
    return this._shortAddress;
  }

  toString() {
    return this.address;
  }

  constructor(address) {
    const fromIndexes = (indexes) => {
      const { rowIndex = 0, columnIndex = 0, rowCount = 1, columnCount = 1 } = indexes;
      this.rowSpan = new RangeSpan({ index: rowIndex, count: rowCount });
      this.columnSpan = new RangeSpan({ index: columnIndex, count: columnCount });
    }

    if (typeof(address) === 'string') {
      const { sheet, ...indexes } = rangeAddressToIndexes(address);
      this.sheet = sheet;
      this._address = address;
      fromIndexes(indexes);
    }
    else {
      const { sheet, rowSpan, columnSpan, ...indexes } = address;
      this.sheet = sheet;
      this._address = address.address;

      if (rowSpan && columnSpan) {
        this.rowSpan = rowSpan;
        this.columnSpan = columnSpan;
      }
      else {
        fromIndexes(indexes);
      }
    }
  }

  isIntersected(another) {
    return this.rowSpan.isIntersected(another.rowSpan)
      && this.columnSpan.isIntersected(another.columnSpan);
  }

  intersect(another) {
    const rowSpan = this.rowSpan.intersect(another.rowSpan);
    const columnSpan = this.columnSpan.intersect(another.columnSpan);

    return rowSpan && columnSpan ? this.#makeCopy({ rowSpan, columnSpan }) : null;
  }

  // ExcelWeb compatibility
  getIntersection(another) {
    return this.intersect(another);
  }

  getBoundingRange(another) {
    return this.#makeCopy({
      rowSpan: this.rowSpan.getBoundingSpan(another.rowSpan),
      columnSpan: this.columnSpan.getBoundingSpan(another.columnSpan),
    });
  }

  getCell(rowIndex, columnIndex) {
    return this.getOffsetAndResizedRange({ rowOffset: rowIndex, columnOffset: columnIndex, rowDelta: 1, columnDelta: 1 }, { useAbsoluteDelta: true });
  }

  getFirstCell() {
    return this.getCell(0, 0);
  }

  getLastCell() {
    return this.getCell(this.rowCount - 1, this.columnCount - 1);
  }

  getAllCells() {
    const res = [];

    for (let row = 0; row < this.rowCount; row++) {
      for (let column = 0; column < this.columnCount; column++) {
        res.push(this.getCell(row, column));
      }
    }
    return res;
  }

  getOffsetRange(rowOffset, columnOffset, useAbsoluteOffset = false) {
    return this.#makeCopy({
      rowSpan: this.rowSpan.getOffsetSpan(rowOffset, useAbsoluteOffset),
      columnSpan: this.columnSpan.getOffsetSpan(columnOffset, useAbsoluteOffset),
    });
  }

  getResizedRange(rowDelta, columnDelta, useAbsoluteDelta = false) {
    return this.#makeCopy({
      rowSpan: this.rowSpan.getResizedSpan(rowDelta, useAbsoluteDelta),
      columnSpan: this.columnSpan.getResizedSpan(columnDelta, useAbsoluteDelta),
    });
  }

  getOffsetAndResizedRange(deltas = {}, options = {}) {
    const { rowDelta, columnDelta, rowOffset, columnOffset } = deltas;
    return this.#makeCopy({
      rowSpan: this.rowSpan.getOffsetAndResizedSpan({ offset: rowOffset, delta: rowDelta }, options),
      columnSpan: this.columnSpan.getOffsetAndResizedSpan({ offset: columnOffset, delta: columnDelta }, options)
    });
  }

  getRow(index) {
    return this.getRows(index, 1);
  }

  getLastRow() {
    return this.getRows(this.rowCount - 1, 1);
  }

  getRows(index, count) {
    return this.#makeCopy({
      rowSpan: this.rowSpan.getOffsetAndResizedSpan({ offset: index, delta: count }, { useAbsoluteDelta: true })
    });
  }

  getRowsAbove(count) {
    return this.getRows(-count, count);
  }

  getRowsBelow(count) {
    return this.getRows(this.rowCount, count);
  }

  getColumn(index) {
    return this.getColumns(index, 1);
  }

  getLastColumn() {
    return this.getColumns(this.columnCount - 1, 1);
  }

  getColumns(index, count) {
    return this.#makeCopy({
      columnSpan: this.columnSpan.getOffsetAndResizedSpan(
        { offset: index, delta: count },
        { useAbsoluteDelta: true }
      )
    });
  }

  getColumnsAfter(count) {
    return this.getColumns(this.columnCount, count);
  }

  getColumnsBefore(count) {
    return this.getColumns(-count, count);
  }

  getEntireRow() {
    return this.#makeCopy({ rowSpan: RangeSpan.getUnboundSpan() });
  }

  getEntireColumn() {
    return this.#makeCopy({ columnSpan: RangeSpan.getUnboundSpan() });
  }

  toRelativeIndexes({ rowIndex = 0, columnIndex = 0 }) {
    return {
      rowIndex: this.rowSpan.toRelativeIndex(rowIndex),
      columnIndex: this.columnSpan.toRelativeIndex(columnIndex)
    };
  }

  toAbsoluteIndexes({ rowIndex = 0, columnIndex = 0 }) {
    return {
      rowIndex: this.rowSpan.toAbsoluteIndex(rowIndex),
      columnIndex: this.columnSpan.toAbsoluteIndex(columnIndex)
    };
  }

  #makeCopy({ rowSpan = this.rowSpan, columnSpan = this.columnSpan }) {
    return new RangeAddress({ sheet: this.sheet, rowSpan, columnSpan });
  }
}


export default RangeAddress;
